import React, { useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";

import { supabase } from "../lib/supabaseClient";
import { API_BASE_URL } from "../config/apiBase";
import { FiDownload, FiRefreshCw, FiAlertCircle } from "react-icons/fi";
import AiDisclosure from "./AiDisclosure";
import "../styles/suiviIA.css";

const SuiviIAComponent = () => {
  const { artworkId } = useParams();
  const [artwork, setArtwork] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const userIdRef = useRef(null)
 
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchProfileAndArtwork();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artworkId]);
  const fetchProfileAndArtwork = async () => {
    try {
      setIsProfileLoading(true);
      setError(null);

      const { data: authData, error: authErr } = await supabase.auth.getUser();
      if (authErr) throw authErr;
      const userId = authData?.user?.id;
      userIdRef.current = userId;
      if (!userId) throw new Error("Utilisateur non authentifié");

      const { data: p, error: pErr } = await supabase
        .from("profiles")
        .select("id, plan, uses_ai")
        .eq("id", userId)
        .single();
      if (pErr) throw pErr;

      setProfile(p);

      // ⚠️ IMPORTANT: on charge l'oeuvre seulement si l'utilisateur a le droit
      // (évite de déclencher des appels IA / exposition d’UX inutile)
      // - Standard: pas d'accès IA
      // - Premium sans consentement: pas d'accès IA tant que uses_ai = false
      const canUseIA = p?.plan === "premium" && p?.uses_ai === true;
      if (canUseIA) {
        await fetchArtwork(userIdRef.current);
      } else {
        // On ne charge pas l'œuvre si le module IA est bloqué
        setIsLoading(false);
      }
    } catch (e) {
      console.error("[SuiviIAComponent] profile/artwork load failed:", e);
      setError("Impossible de vérifier vos droits d'accès à l'IA.");
      setIsLoading(false);
    } finally {
      setIsProfileLoading(false);
    }
  };

  const fetchArtwork = async (uid = userIdRef.current) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Récupérer l'œuvre avec ses relations
      const { data, error } = await supabase
        .from("artworks")
        .select(`
          *,
          locations(*),
          ia_suivis(*)
        `)
        .eq("id", artworkId)
        .eq("client_id", uid)
        .single();

      if (error) throw error;

      // Parser les champs JSON si nécessaire
      const parsedData = {
        ...data,
        documents: typeof data.documents === "string" ? JSON.parse(data.documents) : (data.documents || []),
        images: typeof data.images === "string" ? JSON.parse(data.images) : (data.images || []),
        provenance: typeof data.provenance === "string" ? JSON.parse(data.provenance) : (data.provenance || []),
        specifications: typeof data.specifications === "string" ? JSON.parse(data.specifications) : data.specifications || {}
      };
      // Re-fetch canonique des suivis IA (évite les joins relationnels fragiles)
      const { data: suivis, error: sErr } = await supabase
        .from("ia_suivis")
        .select("*")
        .eq("artwork_id", artworkId)
        .order("created_at", { ascending: false });
      if (sErr) throw sErr;
      parsedData.ia_suivis = suivis || [];

      // Trier les suivis par date de création (du plus récent au plus ancien)
      if (parsedData.ia_suivis) {
        parsedData.ia_suivis.sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        );
      }

      const lastSuivi = parsedData.ia_suivis?.[0] || null;

      setArtwork(parsedData);
      setLastUpdate(lastSuivi?.created_at || null);
    } catch (error) {
      console.error("Erreur lors du chargement de l'œuvre :", error);
      setError("Impossible de charger les données de l'œuvre");
    } finally {
      setIsLoading(false);
    }
  };
  
  // -------------------------------------------------------
  // UI blocks (les 3 cas: Standard / Premium sans consent / OK)
  // -------------------------------------------------------
  const StandardUpgradeBlock = () => (
    <div className="p-6 bg-white border border-gray-200 rounded-xl">
      <h2 className="text-xl font-semibold text-gray-900">🤖 Analyses IA & Vision Artkiv</h2>
      <p className="mt-2 text-gray-700">
        Le suivi IA (conservation, valorisation, alertes) est disponible avec <strong>Artkiv Premium</strong>.
      </p>
      <div className="mt-4 flex gap-3">
        <button
          onClick={() => navigate("/pricing")}
          className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
        >
          Découvrir Premium
        </button>
        <button
          onClick={() => navigate("/dashboard")}
          className="px-4 py-2 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200"
        >
          Retour au tableau de bord
        </button>
      </div>
    </div>
  );

  const ConsentRequiredBlock = () => (
    <div className="p-6 bg-white border border-gray-200 rounded-xl">
      <h2 className="text-xl font-semibold text-gray-900">🔒 Analyse intelligente désactivée</h2>
      <p className="mt-2 text-gray-700">
        Pour utiliser les outils IA (Vision Artkiv), vous devez <strong>autoriser le traitement IA</strong>.
        Vos œuvres ne sont jamais analysées sans votre accord explicite.
      </p>
      <div className="mt-4 flex gap-3">
        <button
          onClick={() => navigate("/profile")}
          className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
        >
          Donner mon consentement
        </button>
        <button
          onClick={() => navigate("/dashboard")}
          className="px-4 py-2 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200"
        >
          Plus tard
        </button>
      </div>
      <p className="mt-3 text-sm text-gray-500">
        Astuce: vous trouverez l’option dans <strong>Mon profil → Consentement IA</strong>.
      </p>
    </div>
  );

  // Fonction pour extraire les infos techniques de la description
  const extractTechnicalInfoFromDescription = (description) => {
    if (!description) return {};
    
    const technicalInfo = {};
    
    // Chercher des informations sur le support/médium
    if (description.toLowerCase().includes('tirage lambda')) {
      technicalInfo.printType = 'Tirage lambda';
    }
    
    if (description.toLowerCase().includes('diasec')) {
      technicalInfo.mounting = 'Diasec';
    }
    
    if (description.toLowerCase().includes('châssis aluminium')) {
      technicalInfo.frame = 'Châssis aluminium';
    }
    
    if (description.toLowerCase().includes('photomosaïque') || 
        description.toLowerCase().includes('mosaïque')) {
      technicalInfo.technique = 'Photomosaïque';
    }
    
    if (description.toLowerCase().includes('générative') || 
        description.toLowerCase().includes('en mouvement')) {
      technicalInfo.dynamic = true;
      technicalInfo.generative = true;
    }
    
    return technicalInfo;
  };

  // Fonction pour vérifier la présence d'un certificat
  const hasArtworkCertificate = (artwork) => {
    if (!artwork?.documents) return false;
    
    const documents = Array.isArray(artwork.documents) ? artwork.documents : [];
    return documents.some(doc => 
      doc.type?.toLowerCase().includes('certificat') || 
      doc.name?.toLowerCase().includes('certificat')
    );
  };

  // Fonction pour vérifier la présence d'assurance
  const getArtworkInsurance = (artwork) => {
    return artwork?.insuranceDetails || null;
  };

  // Fonction pour vérifier les photos haute définition
  const hasHighResPhotos = (artwork) => {
    if (!artwork?.images) return false;
    
    const images = Array.isArray(artwork.images) ? artwork.images : [];
    return images.length > 0;
  };

  // Fonction pour récupérer les rapports de condition (à partir des suivis IA)
  const getConditionReports = (artwork) => {
    // Récupérer à partir des suivis IA s'ils existent
    if (artwork?.ia_suivis?.length > 0) {
      const reportsWithCondition = artwork.ia_suivis.filter(suivi => suivi.conservation_status);
      if (reportsWithCondition.length > 0) {
        return reportsWithCondition;
      }
    }
    return [];
  };

  // Fonction pour vérifier la présence de provenance
  const hasProvenance = (artwork) => {
    if (!artwork?.provenance) return false;
    
    const provenance = Array.isArray(artwork.provenance) ? artwork.provenance : [];
    return provenance.length > 0;
  };

  // Fonction pour générer le résumé de conservation
  const fetchConservationStatus = async (artwork) => {
    console.log("🔍 Début fetchConservationStatus");
    const conditionReports = getConditionReports(artwork);
    const technicalInfo = extractTechnicalInfoFromDescription(artwork.description);
    
    // Déterminer le support principal à partir du medium ou de la description
    let support = "non précisé";
    if (artwork.medium) {
      support = artwork.medium;
    } else if (technicalInfo.printType) {
      support = technicalInfo.printType + 
                (technicalInfo.mounting ? ` sur ${technicalInfo.mounting}` : "") + 
                (technicalInfo.frame ? ` avec ${technicalInfo.frame}` : "");
    }
    
    // Déterminer s'il s'agit d'une œuvre numérique/dynamique
    const isDynamic = technicalInfo.dynamic || artwork.description?.toLowerCase().includes('numérique');
    console.log("📊 Données techniques:", { support, technicalInfo, isDynamic });
    try {
      const prompt = `
Tu es expert en conservation préventive UNIQUEMENT. Tu dois fournir UNIQUEMENT des recommandations techniques précises et chiffrées pour la conservation, sans aucun contexte ou introduction.

- Titre : ${artwork.title}
- Type : ${artwork.typeOfArt || ""}
- Medium : ${support}
- Technique spécifique : ${technicalInfo.technique || ""}
- Nature de l'œuvre : ${isDynamic ? "Œuvre générative/dynamique" : "Œuvre fixe"}

FORMAT OBLIGATOIRE (strictement ces 4 points, uniquement des valeurs techniques):
1. Conditions environnementales: [valeur en lux pour lumière] / [température en °C] / [hygrométrie en %]
2. Fréquence d'inspection: [nombre] jours/semaines/mois
3. Manipulation: [liste de 2-3 précautions techniques spécifiques]
4. Risques: [liste de 2-3 risques spécifiques]
${isDynamic ? "5. Maintenance technique: [contraintes spécifiques pour œuvre numérique]" : ""}

Ne mentionne AUCUN élément concernant la provenance, la certification ou la documentation.
`;

      console.log("📝 Prompt généré:", prompt);
      // Use a different endpoint or pass a type parameter
      // Utilisation d'une fonction pour gérer l'appel fetch
      const fetchDataC = async () => {
        const res = await fetch(`${API_BASE_URL}/conservation-guidelines`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            artwork,
            promptType: "conservation", // Add a type identifier
            promptOverride: prompt,
            client_id: artwork?.client_id || null,
          }),
        });

        if (!res.ok) {
          throw new Error(`API request failed with status ${res.status}`);
        }

        const data = await res.json();
        console.log("Données reçues:", data); // Pour déboguer
        if (!data.conservation) {
          throw new Error("Pas de recommandations retournées");
        }
        return data.conservation;
      };

      // Appel de la fonction fetch
      return await fetchDataC();
    } catch (err) {
      console.warn("❗ OpenAI indisponible, fallback local utilisé");

      // Création d'un fallback plus spécifique basé sur le type d'œuvre
      const fallbackParts = [];

      if (technicalInfo.printType === 'Tirage lambda' || support.includes('tirage')) {
        fallbackParts.push("Pour ce tirage photographique, maintenir une température stable (18-21°C) et une humidité relative de 30-50%. Éviter l'exposition directe aux UV qui accélèrerait la dégradation des couleurs.");
      } else if (isDynamic) {
        fallbackParts.push("Cette œuvre dynamique/générative nécessite une vérification technique régulière des composants et une documentation précise de son fonctionnement pour assurer sa pérennité.");
      } else if (technicalInfo.mounting === 'Diasec') {
        fallbackParts.push("Le montage diasec nécessite une inspection des bords pour détecter toute délamination ou infiltration d'humidité. Éviter les fluctuations importantes de température qui pourraient affecter l'adhésion.");
      } else {
        fallbackParts.push("Conservation préventive recommandée : contrôle régulier des conditions environnementales et inspection trimestrielle pour détecter tout signe précoce de détérioration.");
      }

      if (conditionReports.length === 0) {
        fallbackParts.push("Un rapport de condition initial est fortement recommandé pour établir un état de référence.");
      }

      return fallbackParts.join(" ");
    }
  };

  // Fonction pour générer le résumé exécutif
  const fetchExecutiveSummary = async (artwork) => {
    const conditionReports = getConditionReports(artwork);
    const hasInsurance = !!getArtworkInsurance(artwork);
    const hasCertificate = hasArtworkCertificate(artwork);
    const hasProvenanceData = hasProvenance(artwork);
    const hasPhotos = hasHighResPhotos(artwork);
    
    
    
    
    try {
      // Utilisation d'une fonction pour gérer l'appel fetch
      const fetchData = async () => {
        const res = await fetch(`${API_BASE_URL}/generate-summary`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            artwork,
            compliance: {
              certificate: hasCertificate,
              insurance: hasInsurance,
              condition_report: conditionReports.length > 0,
              provenance: hasProvenanceData,
              high_res_photos: hasPhotos
            },
            client_id: artwork?.client_id || null,
          }),
        });
        
        if (!res.ok) {
          throw new Error(`API request failed with status ${res.status}`);
        }
        
        const data = await res.json();
        return data.summary;
      };
      
      // Appel de la fonction fetch
      return await fetchData();
    } catch (error) {
      console.warn("Erreur lors de la génération du résumé:", error);
      // Fallback si l'API n'est pas disponible
      let fallback = `${artwork.title} de ${artwork.artist || "auteur inconnu"}. `;
      
      // Construire un message de fallback informatif
      const missingItems = [];
      if (!hasCertificate) missingItems.push("certificat d'authenticité");
      if (!hasInsurance) missingItems.push("assurance");
      if (conditionReports.length === 0) missingItems.push("rapport de condition");
      if (!hasProvenanceData) missingItems.push("provenance documentée");
      if (!hasPhotos) missingItems.push("documentation photographique HD");
      
      if (missingItems.length > 0) {
        fallback += `Des informations importantes manquent: ${missingItems.join(", ")}. `;
        fallback += `La complétion de ces éléments est recommandée pour sécuriser et valoriser l'œuvre.`;
      } else {
        fallback += `La documentation de l'œuvre est complète. Un suivi régulier est recommandé pour maintenir sa valeur.`;
      }
      
      return fallback;
    }
  };

  const generateIASynthesis = async () => {
    try {
      setIsGenerating(true);
      setError(null);
      
      if (!artwork) throw new Error("Données de l'œuvre non disponibles");
      
      // Analyser les données de l'œuvre
      const hasCertificate = hasArtworkCertificate(artwork);
      const insuranceDetails = getArtworkInsurance(artwork);
      const hasInsurance = !!insuranceDetails;
      const conditionReports = getConditionReports(artwork);
      const hasRecentConditionReport = conditionReports.length > 0;
      const hasProvenanceData = hasProvenance(artwork);
      const technicalInfo = extractTechnicalInfoFromDescription(artwork.description);
      
      // Vérifier si des détails techniques importants sont absents
      const isDynamic = technicalInfo.dynamic || artwork.description?.toLowerCase().includes('numérique');
      const needsTechnicalDocumentation = isDynamic && !artwork.documents?.some(doc => 
        doc.type?.toLowerCase().includes('technique') || 
        doc.name?.toLowerCase().includes('technique'));
      
      // Créer les recommandations
      const recommendations = [];
      
      if (!hasInsurance) {
        recommendations.push({
          priority: "haute",
          text: "Souscrire une assurance adaptée à la valeur actuelle de l'œuvre"
        });
      }
      
      if (!hasRecentConditionReport) {
        recommendations.push({
          priority: "moyenne",
          text: "Planifier un examen de conservation dans les 3 prochains mois"
        });
      }
      
      if (!hasProvenanceData) {
        recommendations.push({
          priority: "moyenne",
          text: "Documenter la provenance de l'œuvre"
        });
      }
      
      if (!hasHighResPhotos) {
        recommendations.push({
          priority: "moyenne",
          text: "Ajouter des photographies haute définition de l'œuvre"
        });
      }
      
      // Ajouter une recommandation technique spécifique si nécessaire
      if (needsTechnicalDocumentation) {
        recommendations.push({
          priority: "haute",
          text: "Ajouter une documentation technique détaillée pour cette œuvre générative/dynamique"
        });
      }
      
      // Recommandations spécifiques basées sur le type d'œuvre
      if (technicalInfo.printType === 'Tirage lambda' || artwork.medium?.toLowerCase().includes('tirage')) {
        recommendations.push({
          priority: "moyenne",
          text: "Vérifier les conditions de luminosité et l'exposition aux UV pour préserver le tirage photographique"
        });
      }
      
      if (technicalInfo.mounting === 'Diasec') {
        recommendations.push({
          priority: "basse",
          text: "Contrôler l'intégrité du montage diasec et l'absence de bulles ou délamination"
        });
      }
      
      if (technicalInfo.frame === 'Châssis aluminium') {
        recommendations.push({
          priority: "basse",
          text: "Vérifier l'état du châssis aluminium et l'absence d'oxydation"
        });
      }
      
      // Toujours ajouter une suggestion pour la visibilité
      recommendations.push({
        priority: "suggestion",
        text: "Envisager une exposition ou un prêt pour augmenter la visibilité de l'œuvre"
      });
      
      // Créer les alertes
      const alerts = [];
      
      // Alerte de maintenance avec fréquence adaptée au type d'œuvre
      const maintenanceInterval = isDynamic ? 60 : 90; // 2 mois pour œuvres dynamiques, 3 mois pour œuvres fixes
      alerts.push({
        type: "maintenance",
        text: `Prochain contrôle recommandé avant le ${new Date(Date.now() + maintenanceInterval * 24 * 60 * 60 * 1000).toLocaleDateString("fr-FR")}`
      });
      
      // Alerte spécifique pour les œuvres génératives/dynamiques
      if (isDynamic) {
        alerts.push({
          type: "maintenance",
          text: "Œuvre générative/dynamique: vérification technique recommandée pour garantir le bon fonctionnement"
        });
      }
      
      // Alerte spécifique au support technique
      if (technicalInfo.mounting === 'Diasec') {
        alerts.push({
          type: "maintenance",
          text: "Surveillez les conditions ambiantes pour les montages Diasec: éviter l'humidité variable et les températures élevées"
        });
      }
      
      if (hasInsurance && insuranceDetails?.endDate) {
        const expiryDate = new Date(insuranceDetails.endDate);
        if (expiryDate < new Date()) {
          alerts.push({
            type: "market",
            text: `Assurance expirée depuis le ${expiryDate.toLocaleDateString("fr-FR")}`
          });
        } else if (expiryDate < new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)) {
          alerts.push({
            type: "market",
            text: `Assurance expire le ${expiryDate.toLocaleDateString("fr-FR")}, renouvellement à prévoir`
          });
        }
      }
      
      alerts.push({
        type: "opportunity",
        text: "Possibilités d'expositions thématiques identifiées pour l'année à venir"
      });
      
      // Créer l'historique
      const history = [
        {
          date: new Date().toISOString(),
          action: "Génération de la synthèse IA",
          actor: "Système ARTKIV"
        }
      ];
      
      if (conditionReports.length > 0) {
        history.push({
          date: conditionReports[0].created_at,
          action: "Dernier rapport de condition",
          actor: "Service documentation"
        });
      }
      
      if (artwork.updated_at) {
        history.push({
          date: artwork.updated_at,
          action: "Mise à jour des informations de l'œuvre",
          actor: "Utilisateur ARTKIV"
        });
      }

      // Créer le résumé de conservation avec OpenAI
      const conservationStatus = await fetchConservationStatus(artwork);
      
      // Éviter les répétitions dans le résumé exécutif et la conservation
      const summary = await fetchExecutiveSummary(artwork);

      // Créer le document de synthèse
      const synthesisData = {
        artwork_id: artworkId,
        summary: summary,
        market_trend: "Les tendances du marché pour les œuvres similaires montrent un regain d'intérêt (+12% de valeur moyenne sur les 12 derniers mois).",
        conservation_status: conservationStatus,
        document_compliance: {
          certificate: hasCertificate,
          insurance: hasInsurance,
          condition_report: hasRecentConditionReport,
          provenance: hasProvenanceData,
          high_res_photos: hasHighResPhotos
        },
        recommendations: recommendations,
        alerts: alerts,
        history: history,
        created_at: new Date().toISOString()
      };
      
      // Enregistrer la synthèse
      const { error: insertError } = await supabase
        .from("ia_suivis")
        .insert([synthesisData]);
        
      if (insertError) throw new Error("Erreur lors de l'enregistrement de la synthèse");
      
      // Rafraîchir les données
      await fetchArtwork(userIdRef.current);
    } catch (error) {
      console.error("Erreur lors de la génération de la synthèse IA:", error);
      setError("Une erreur est survenue lors de la génération de la synthèse IA");
    } finally {
      setIsGenerating(false);
    }
  };

  const exportAsPDF = () => {
    alert("Fonctionnalité d'export PDF à implémenter");
  };
  // -----------------------------+  // Guards d’accès (les 3 cas)
  // ----------------------------
  if (isProfileLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const plan = profile?.plan || "standard";
  const usesAI = profile?.uses_ai === true;

  // Cas 1: Standard -> upgrade
  if (plan === "standard") {
    return <StandardUpgradeBlock />;
  }

  // Cas 2: Premium sans consentement -> demander consentement
  if (plan === "premium" && !usesAI) {
    return <ConsentRequiredBlock />;
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!artwork) {
    return (
      <div className="p-6">
        <h2 className="text-xl font-bold text-red-600">Œuvre introuvable</h2>
        <p className="mt-2">L'œuvre avec l'ID "{artworkId}" n'a pas été trouvée dans la base de données.</p>
        <button 
          onClick={() => navigate('/dashboard')} 
          className="mt-6 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Retour au tableau de bord
        </button>
      </div>
    );
  }

  const latestSuivi = artwork.ia_suivis?.[0] || null;

  return (
    <div className="max-w-6xl mx-auto pb-16">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      <header className="flex justify-between items-center border-b border-gray-200 px-6 py-4">
        <h1 className="text-2xl font-semibold">Suivi IA - {artwork.title}</h1>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">
            {lastUpdate ? `Dernière mise à jour: ${new Date(lastUpdate).toLocaleDateString('fr-FR')}` : "Aucune analyse IA disponible"}
          </span>
          <button 
            onClick={generateIASynthesis}
            disabled={isGenerating}
            className="ml-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
          >
            <FiRefreshCw className={isGenerating ? "animate-spin" : ""} />
            {isGenerating ? "Génération en cours..." : "Régénérer l'analyse"}
          </button>
        </div>
      </header>

      {!latestSuivi ? (
        <div className="p-8 text-center">
          <FiAlertCircle className="mx-auto h-12 w-12 text-yellow-500 mb-4" />
          <h3 className="text-lg font-medium text-gray-900">Aucune analyse IA disponible</h3>
          <p className="mt-2 text-gray-500">
            Générez une analyse IA pour obtenir des recommandations intelligentes et un suivi de conformité documentaire.
          </p>
          <button 
            onClick={generateIASynthesis}
            disabled={isGenerating}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            {isGenerating ? "Génération en cours..." : "Générer une analyse IA"}
          </button>
        </div>
      ) : (
        <div className="p-6">
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              Synthèse IA – Suivi intelligent de l'œuvre
            </h2>
            <AiDisclosure
              type="assisted"
              className="mb-4"
              note="Cette analyse aide à repérer des éléments et à explorer des tendances. Elle ne remplace pas l'examen physique d'un restaurateur ni la validation d'un professionnel qualifié."
            />

            {/* Résumé exécutif */}
            <div className="mb-6">
              <h3 className="text-lg font-medium mb-2">Résumé exécutif</h3>
              <p className="text-gray-700">{latestSuivi.summary}</p>
              
              {/* Tendances du marché - ligne séparée */}
              <div className="mt-4 pt-3 border-t border-gray-100">
                <h4 className="text-md font-medium mb-1">Tendances du marché</h4>
                <p className="text-gray-700">{latestSuivi.market_trend}</p>
              </div>
              
              {/* Statut de conservation - section distincte */}
 
              <div className="mt-4 pt-3 border-t border-gray-100">
                <h4 className="text-md font-medium mb-1">Statut de conservation</h4>
                {latestSuivi.conservation_status ? (
                  <div className="space-y-3">
                    {latestSuivi.conservation_status.split(/(\d+\.)/).filter(Boolean).map((part, index) => {
                      // Si c'est un numéro de point
                      if (/^\d+\.$/.test(part)) {
                        return (
                          <div key={index} className="font-semibold mt-2">
                            {part}
                          </div>
                        );
                      } 
                      // Sinon c'est le contenu
                      return (
                        <div key={index} className="ml-6">
                          {part}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-700">Aucune information de conservation disponible.</p>
                )}
              </div>
            </div>
            
            {/* Conformité documentaire */}
            <div className="mb-6">
              <h3 className="text-lg font-medium mb-2">Conformité documentaire</h3>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className={`inline-flex items-center justify-center h-5 w-5 rounded-full mr-2 ${latestSuivi.document_compliance.certificate ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                    {latestSuivi.document_compliance.certificate ? "✓" : "⚠️"}
                  </span>
                  <span><strong>Certificat d'authenticité:</strong> {latestSuivi.document_compliance.certificate ? "présent" : "manquant"}</span>
                </li>
                <li className="flex items-start">
                  <span className={`inline-flex items-center justify-center h-5 w-5 rounded-full mr-2 ${latestSuivi.document_compliance.insurance ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                    {latestSuivi.document_compliance.insurance ? "✓" : "⚠️"}
                  </span>
                  <span><strong>Police d'assurance:</strong> {latestSuivi.document_compliance.insurance ? "présente" : "manquante"} {!latestSuivi.document_compliance.insurance && <em className="text-red-600">Ajout recommandé</em>}</span>
                </li>
                <li className="flex items-start">
                  <span className={`inline-flex items-center justify-center h-5 w-5 rounded-full mr-2 ${latestSuivi.document_compliance.condition_report ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                    {latestSuivi.document_compliance.condition_report ? "✓" : "⚠️"}
                  </span>
                  <span><strong>Rapport de condition:</strong> {latestSuivi.document_compliance.condition_report ? "présent" : "non fourni depuis plus de 24 mois"} {!latestSuivi.document_compliance.condition_report && <em className="text-yellow-600">Une mise à jour est suggérée</em>}</span>
                </li>
                <li className="flex items-start">
                  <span className={`inline-flex items-center justify-center h-5 w-5 rounded-full mr-2 ${latestSuivi.document_compliance.provenance ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                    {latestSuivi.document_compliance.provenance ? "✓" : "⚠️"}
                  </span>
                  <span><strong>Provenance:</strong> {latestSuivi.document_compliance.provenance ? "renseignée et vérifiée" : "incomplète"}</span>
                </li>
                <li className="flex items-start">
                  <span className={`inline-flex items-center justify-center h-5 w-5 rounded-full mr-2 ${latestSuivi.document_compliance.high_res_photos ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                    {latestSuivi.document_compliance.high_res_photos ? "✓" : "⚠️"}
                  </span>
                  <span><strong>Photos haute définition:</strong> {latestSuivi.document_compliance.high_res_photos ? "disponibles" : "manquantes"}</span>
                </li>
              </ul>
            </div>
            
            {/* Recommandations automatisées */}
            {latestSuivi.recommendations && latestSuivi.recommendations.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-medium mb-2">Recommandations automatisées</h3>
                <ul className="space-y-2">
                  {latestSuivi.recommendations.map((rec, index) => (
                    <li key={index} className="flex items-center">
                      <span className={`
                        inline-flex items-center justify-center h-5 w-5 rounded-full mr-2
                        ${rec.priority === "haute" ? "bg-red-100 text-red-700" : 
                          rec.priority === "moyenne" ? "bg-yellow-100 text-yellow-700" : 
                          "bg-blue-100 text-blue-700"}
                      `}>
                        {rec.priority === "haute" ? "!" : 
                         rec.priority === "moyenne" ? "?" : 
                         "i"}
                      </span>
                      <span>
                        <strong className={`
                          ${rec.priority === "haute" ? "text-red-700" : 
                            rec.priority === "moyenne" ? "text-yellow-700" : 
                            "text-blue-700"}
                        `}>
                          Priorité {rec.priority}:
                        </strong> {rec.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Alertes & notifications */}
            {latestSuivi.alerts && latestSuivi.alerts.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-medium mb-2">Alertes & notifications</h3>
                <ul className="space-y-2">
                  {latestSuivi.alerts.map((alert, index) => (
                    <li key={index} className="flex items-start">
                      <span className="mr-2">
                        {alert.type === "maintenance" ? "📅" : 
                         alert.type === "market" ? "📊" : 
                         "🔍"}
                      </span>
                      <span><strong>{
                        alert.type === "maintenance" ? "Rappel maintenance" : 
                        alert.type === "market" ? "Marché" : 
                        "Veille"
                      }:</strong> {alert.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Historique des actions */}
            {latestSuivi.history && latestSuivi.history.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-medium mb-2">Historique des actions</h3>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Intervenant</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {latestSuivi.history.map((item, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(item.date).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.action}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.actor}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Export PDF */}
            <div className="mt-8 text-center">
              <button 
                onClick={exportAsPDF}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FiDownload className="mr-2" />
                Exporter la synthèse en PDF
              </button>
              <p className="mt-2 text-xs text-gray-500">
                Un clic pour générer un rapport PDF de cette synthèse, à archiver ou partager
              </p>
            </div>
          </div>
          
          {/* Actions rapides basées sur l'analyse */}
          <div className="flex flex-wrap gap-4 justify-center">
            {latestSuivi.document_compliance && !latestSuivi.document_compliance.insurance && (
              <Link 
                to="#"
                onClick={(e) => {
                  e.preventDefault();
                  window.dispatchEvent(new CustomEvent("triggerInsuranceModal"));
                }}
                className="flex-1 min-w-[200px] max-w-xs bg-red-50 border border-red-200 rounded-lg p-4 text-center hover:bg-red-100 transition-colors"
              >
                <div className="text-2xl mb-2">🔒</div>
                <h3 className="font-medium text-red-800">Ajouter une assurance</h3>
                <p className="text-xs text-red-600 mt-1">Priorité haute</p>
              </Link>
            )}
            
            {latestSuivi.document_compliance && !latestSuivi.document_compliance.condition_report && (
              <Link 
                to={`/conservation/${artworkId}/suivi/edit`}
                className="flex-1 min-w-[200px] max-w-xs bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center hover:bg-yellow-100 transition-colors"
              >
                <div className="text-2xl mb-2">📝</div>
                <h3 className="font-medium text-yellow-800">Créer un rapport de condition</h3>
                <p className="text-xs text-yellow-600 mt-1">Priorité moyenne</p>
              </Link>
            )}
            
            <Link 
              to={`/maintenance/${artworkId}`}
              className="flex-1 min-w-[200px] max-w-xs bg-blue-50 border border-blue-200 rounded-lg p-4 text-center hover:bg-blue-100 transition-colors"
            >
              <div className="text-2xl mb-2">🔧</div>
              <h3 className="font-medium text-blue-800">Planifier la maintenance</h3>
              <p className="text-xs text-blue-600 mt-1">Prochaine échéance recommandée</p>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuiviIAComponent;